/**
 * VariantMetricsTable — Story 11.7.
 *
 * Tabela comparativa das variantes A/B/C/N de uma campanha, consumindo
 * dados do endpoint `GET /admin/campaigns/:id/variant-metrics` (Story 11.6).
 *
 * Ordenação default: `conversion_rate` DESC com nulls last (champion no topo).
 */

import { useMemo } from 'react';

import { formatRate, formatDuration } from '@/lib/metrics-format';

export interface VariantMetricRow {
  variant_id: string;
  variant_letter: string;
  meta_template_name: string;
  active: boolean;
  position: number;
  impressions: number;
  responses: number;
  conversions: number;
  response_rate: number | null;
  conversion_rate: number | null;
  avg_response_time_ms: number | null;
  is_champion: boolean;
  low_volume_warning: boolean;
}

interface Props {
  variants: VariantMetricRow[];
  /** Quando true, suprime champion highlight (e.g. total_active_variants <= 1). */
  suppressChampion?: boolean;
}

function sortVariants(variants: VariantMetricRow[]): VariantMetricRow[] {
  return [...variants].sort((a, b) => {
    const ar = a.conversion_rate;
    const br = b.conversion_rate;
    if (ar === null && br === null) return a.variant_letter.localeCompare(b.variant_letter);
    if (ar === null) return 1; // nulls last
    if (br === null) return -1;
    return br - ar; // DESC
  });
}

export function VariantMetricsTable({ variants, suppressChampion = false }: Props) {
  const sorted = useMemo(() => sortVariants(variants), [variants]);

  return (
    <div className="overflow-x-auto rounded-md border" data-testid="variant-metrics-table-wrapper">
      <table
        className="w-full text-sm"
        role="table"
        aria-label="Métricas comparativas das variantes da campanha"
      >
        <thead className="sticky top-0 border-b bg-muted/60 text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th scope="col" className="px-3 py-2 font-medium">
              Variante
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Template
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Pos.
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Impressions
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Responses
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Conversions
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Avg response
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Status
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((v) => {
            const isChampion = v.is_champion && !suppressChampion;
            return (
              <tr
                key={v.variant_id}
                data-testid={`metrics-row-${v.variant_letter}`}
                className={
                  'border-b last:border-b-0 ' +
                  (isChampion ? 'bg-emerald-500/5 ' : '') +
                  (!v.active ? 'opacity-60 ' : '')
                }
              >
                <td className="px-3 py-2 align-middle">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold uppercase text-primary"
                      aria-label={`Variante ${v.variant_letter}`}
                    >
                      {v.variant_letter}
                    </span>
                    {!v.active && (
                      <span
                        className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase text-muted-foreground"
                        aria-label="Variante inativa"
                      >
                        inativa
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 align-middle font-mono text-xs">{v.meta_template_name}</td>
                <td className="px-3 py-2 text-right align-middle">{v.position}</td>
                <td className="px-3 py-2 text-right align-middle">{v.impressions}</td>
                <td className="px-3 py-2 text-right align-middle">
                  {v.responses}{' '}
                  <span className="text-xs text-muted-foreground">
                    ({formatRate(v.response_rate)})
                  </span>
                </td>
                <td className="px-3 py-2 text-right align-middle">
                  {v.conversions}{' '}
                  <span className="text-xs text-muted-foreground">
                    ({formatRate(v.conversion_rate)})
                  </span>
                </td>
                <td className="px-3 py-2 text-right align-middle text-xs">
                  {formatDuration(v.avg_response_time_ms)}
                </td>
                <td className="px-3 py-2 align-middle">
                  <div className="flex flex-wrap items-center gap-1">
                    {isChampion && (
                      <span
                        className="inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400"
                        data-testid={`champion-badge-${v.variant_letter}`}
                        title="Maior conversion_rate na janela atual"
                        aria-label={`Champion da campanha — variante ${v.variant_letter}`}
                      >
                        🏆 Champion
                      </span>
                    )}
                    {v.low_volume_warning && (
                      <span
                        className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-400"
                        data-testid={`low-volume-badge-${v.variant_letter}`}
                        title="Menos de 30 impressions — conversion_rate estatisticamente fraco. Aguarde mais disparos antes de decidir."
                        aria-label="Warning: dados insuficientes pra decisão estatística"
                      >
                        ⚠️ Dados insuficientes
                      </span>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
